import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useState } from 'react'
import { useWatch } from 'react-hook-form'
import { AlertCircle, Check, ChevronDown, Zap } from 'lucide-react'
import type { Control, UseFormSetValue } from 'react-hook-form'
import type { FormValues } from './agent-detail.schema'
import type { Scenario, AgentTrigger } from '@super/shared-types'
import {
  WEBHOOK_EVENT_TO_TRIGGER,
  WEBHOOK_TRIGGER_TO_EVENT,
} from '@super/shared-types'
import { cn } from '@/lib/utils'

const INBOUND_CALL_VALUE = 'INBOUND_CALL'

type TriggerRowProps = {
  control: Control<FormValues>
  setValue: UseFormSetValue<FormValues>
  direction: 'INBOUND' | 'OUTBOUND'
  scenarios: Scenario[]
  triggers: AgentTrigger[]
  usedScenarioIds: Set<string>
  usedTriggerEventNames?: Set<string>
  hasDefaultAgent: boolean
  currentDefaultAgentName: string | null
}

// Options are DB Trigger rows; resolve the selected value against them — custom
// by triggerId, built-in/legacy by eventName.
function getSelectedTriggerId(
  triggerType: FormValues['triggerType'],
  scenarioId: FormValues['scenarioId'],
  triggerId: FormValues['triggerId'],
  triggers: AgentTrigger[],
): string {
  if (triggerId) {
    return triggers.find(option => option.triggerId === triggerId)?.id ?? ''
  }
  if (triggerType === 'SCENARIO' && scenarioId) return scenarioId
  if (triggerType) {
    const eventName = WEBHOOK_TRIGGER_TO_EVENT[triggerType]
    if (eventName) {
      const match = triggers.find(option => option.eventName === eventName)
      if (match) return match.id
    }
  }
  return ''
}

export function TriggerRow({
  control,
  setValue,
  direction,
  scenarios,
  triggers,
  usedScenarioIds,
  usedTriggerEventNames,
  hasDefaultAgent,
  currentDefaultAgentName,
}: TriggerRowProps) {
  const triggerType = useWatch({ control, name: 'triggerType' })
  const scenarioId = useWatch({ control, name: 'scenarioId' })
  const triggerId = useWatch({ control, name: 'triggerId' })

  const isInbound = direction === 'INBOUND'
  const selectedInboundValue =
    triggerType === 'SCENARIO' && scenarioId ? scenarioId : INBOUND_CALL_VALUE
  const selectedOutboundId = getSelectedTriggerId(
    triggerType,
    scenarioId,
    triggerId,
    triggers,
  )

  const showReplacingWarning =
    isInbound &&
    selectedInboundValue === INBOUND_CALL_VALUE &&
    currentDefaultAgentName !== null

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-1.5 shrink-0 text-sm font-medium text-muted-foreground whitespace-nowrap">
          <Zap className="w-3.5 h-3.5" />
          Trigger
        </label>

        {isInbound ? (
          <Select
            value={selectedInboundValue}
            onValueChange={value => {
              if (value === INBOUND_CALL_VALUE) {
                setValue('triggerType', 'INBOUND_CALL', { shouldDirty: true })
                setValue('scenarioId', null, { shouldDirty: true })
              } else {
                setValue('triggerType', 'SCENARIO', { shouldDirty: true })
                setValue('scenarioId', value, { shouldDirty: true })
              }
            }}
          >
            <SelectTrigger className="w-full max-w-sm">
              <SelectValue placeholder="Select trigger..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={INBOUND_CALL_VALUE}>
                Receptionist{' '}
                <span className="text-xs text-muted-foreground">
                  (First to Answer)
                </span>
              </SelectItem>
              {hasDefaultAgent &&
                scenarios.map(scenario => (
                  <SelectItem
                    key={scenario.id}
                    value={scenario.id}
                    disabled={usedScenarioIds.has(scenario.id)}
                  >
                    SCENARIO | {scenario.name}
                    {usedScenarioIds.has(scenario.id) &&
                      ' (assigned to another agent)'}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        ) : (
          <OutboundTriggerCombobox
            triggers={triggers}
            selectedId={selectedOutboundId}
            usedEventNames={usedTriggerEventNames}
            onSelect={selectedId => {
              const trigger = triggers.find(t => t.id === selectedId)
              if (!trigger) return
              if (trigger.type === 'SCENARIO') {
                setValue('triggerType', 'SCENARIO', { shouldDirty: true })
                setValue('scenarioId', trigger.scenarioId ?? trigger.id, {
                  shouldDirty: true,
                })
                setValue('triggerId', null, { shouldDirty: true })
                setValue('triggerEventName', null, { shouldDirty: true })
              } else if (trigger.triggerId) {
                // Custom Trigger — link by id + eventName.
                setValue('triggerType', undefined, { shouldDirty: true })
                setValue('scenarioId', null, { shouldDirty: true })
                setValue('triggerId', trigger.triggerId, { shouldDirty: true })
                setValue('triggerEventName', trigger.eventName ?? null, {
                  shouldDirty: true,
                })
              } else {
                // Built-in webhook (Lead / Collections).
                const mapped = trigger.eventName
                  ? WEBHOOK_EVENT_TO_TRIGGER[trigger.eventName]
                  : undefined
                setValue('triggerType', mapped ?? 'LEAD_WEBHOOK', {
                  shouldDirty: true,
                })
                setValue('scenarioId', null, { shouldDirty: true })
                setValue('triggerId', null, { shouldDirty: true })
                setValue('triggerEventName', null, { shouldDirty: true })
              }
            }}
          />
        )}
      </div>

      {isInbound && (
        <div className="relative min-h-4 ml-[5.5rem]">
          <div
            className={`absolute inset-0 transition-all duration-200 ease-in-out transform ${
              showReplacingWarning
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 -translate-y-1 pointer-events-none'
            }`}
          >
            <p className="flex items-center gap-1 text-xs text-warning">
              <AlertCircle className="w-3 h-3 shrink-0" />
              &quot;{currentDefaultAgentName}&quot; will be deactivated in favor
              of this agent.
            </p>
          </div>
          <div
            className={`absolute inset-0 transition-all duration-200 ease-in-out transform ${
              showReplacingWarning
                ? 'opacity-0 translate-y-1 pointer-events-none'
                : 'opacity-100 translate-y-0'
            }`}
          >
            <p className="text-xs text-muted-foreground">
              What event triggers the activation of this agent?
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

const outboundTriggerLabel = (trigger: AgentTrigger): string =>
  `${trigger.type} | ${trigger.name ?? trigger.scenarioId ?? trigger.id}`

function OutboundTriggerCombobox({
  triggers,
  selectedId,
  usedEventNames,
  onSelect,
}: {
  triggers: AgentTrigger[]
  selectedId: string
  usedEventNames?: Set<string>
  onSelect: (id: string) => void
}) {
  const [open, setOpen] = useState(false)
  const selected = triggers.find(trigger => trigger.id === selectedId)
  const showSearch = triggers.length > 5

  // Taken by another active agent on a shared channel — except the current
  // selection, which must stay visible/selectable.
  const isTaken = (trigger: AgentTrigger): boolean =>
    trigger.id !== selectedId &&
    !!trigger.eventName &&
    !!usedEventNames?.has(trigger.eventName)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full max-w-sm justify-between font-normal"
        >
          <span className="truncate">
            {selected ? outboundTriggerLabel(selected) : 'Select trigger...'}
          </span>
          <ChevronDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command>
          {showSearch && (
            <CommandInput placeholder="Search triggers..." variant="popover" />
          )}
          <CommandList>
            <CommandEmpty>No trigger found.</CommandEmpty>
            <CommandGroup>
              {triggers.map(trigger => {
                const taken = isTaken(trigger)
                return (
                  <CommandItem
                    key={trigger.id}
                    value={outboundTriggerLabel(trigger)}
                    disabled={taken}
                    onSelect={() => {
                      if (taken) return
                      onSelect(trigger.id)
                      setOpen(false)
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 size-4',
                        selectedId === trigger.id ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    {outboundTriggerLabel(trigger)}
                    {taken && (
                      <span className="ml-auto text-xs text-muted-foreground">
                        in use
                      </span>
                    )}
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
